import { useEffect, useState, useMemo } from 'react';
import { createRoute, redirect, useParams, Link } from '@tanstack/react-router';
import { useAuth0 } from '@auth0/auth0-react';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  SortingState,
  flexRender,
} from '@tanstack/react-table';
import { Route as rootRoute } from '../__root';
import { Layout } from '@/client/layouts/layout';
import { useClientsStore } from '@/client/store/clientsStore';
import { useClientUsersStore } from '@/client/store/clientUsersStore';
import { useClientDetailsStore } from '@/client/store/clientDetailsStore';
import { useEncodersStore } from '@/client/store/encodersStore';
import { useAllPersistentStreamsStore } from '@/client/store/allPersistentStreamsStore';
import { useClientPlatformStore } from '@/client/store/clientPlatformStore';
import { buildClientSummary } from '@/client/lib/clientSummary';
import { CLIENT_PRIMARY_CONTACTS } from '@shared/config/clientContacts';
import { toast } from 'sonner';
import { columns } from '@/client/components/tables/client-users-columns';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/client/components/ui/table';
import { Button } from '@/client/components/ui/button';
import { CacheTimestamp } from '@/client/components/cache-timestamp';
import { SwapClientDialog, useClientSwapInfo } from '@/client/components/swap-client-dialog';
import {
  AccountInfoCard,
  PackageCard,
  WebhooksCard,
  TechsCard,
  ContactsCard,
  PlatformSummaryCard,
  VendorsCard,
  ApiKeysCard,
} from '@/client/components/client-details';
import { ArrowLeft, ArrowRight, Check, ClipboardCopy, Copy, RefreshCw, ScrollText, Webhook } from 'lucide-react';

export const Route = createRoute({
  getParentRoute: () => rootRoute,
  path: 'clients/$clientID',
  beforeLoad: async ({ context }) => {
    if (!context.auth.isAuthenticated) {
      throw redirect({
        to: '/',
      });
    }
  },
  component: ClientDetailComponent,
});

function ClientDetailComponent() {
  const { clientID } = useParams({ from: '/clients/$clientID' });
  const { getAccessTokenSilently, logout } = useAuth0();
  const [sorting, setSorting] = useState<SortingState>([]);
  const [swapDialogOpen, setSwapDialogOpen] = useState(false);
  const [clientIDCopied, setClientIDCopied] = useState(false);

  const { clients, fetchClients } = useClientsStore();
  const { needsSwap } = useClientSwapInfo(clientID);
  const client = clients.find(c => c.clientID === clientID);

  const { loading: usersLoading, error: usersError, fetchUsers, getUsers, getCacheInfo } = useClientUsersStore();
  const users = getUsers(clientID);
  const usersCacheInfo = getCacheInfo(clientID);

  // Client details store
  const {
    loading: detailsLoading,
    error: detailsError,
    fetchDetails,
    getDetails,
  } = useClientDetailsStore();
  const details = getDetails(clientID);

  const { fetchSummary: fetchPlatformSummary, getSummary: getPlatformSummary } = useClientPlatformStore();
  const platformSummary = getPlatformSummary(clientID);

  const primaryContacts = useMemo(() => {
    const ids = CLIENT_PRIMARY_CONTACTS[clientID] ?? [];
    return users.filter(u => ids.includes(u.userId));
  }, [users, clientID]);

  // Use global stores and filter by clientID
  const { encoders, fetchEncoders } = useEncodersStore();
  const { streams: persistentStreams, fetchStreams: fetchPersistentStreams } = useAllPersistentStreamsStore();

  const clientEncoders = useMemo(
    () => encoders.filter(e => e.clientID === clientID),
    [encoders, clientID]
  );
  const onlineEncoders = useMemo(
    () => clientEncoders.filter(e => e.status === 1),
    [clientEncoders]
  );
  const clientPersistentStreams = useMemo(
    () => persistentStreams.filter(s => s.clientID === clientID),
    [persistentStreams, clientID]
  );

  useEffect(() => {
    const loadData = async () => {
      try {
        const token = await getAccessTokenSilently();
        if (clients.length === 0) {
          await fetchClients(token, logout);
        }
        await Promise.all([
          fetchUsers(clientID, logout, token),
          fetchDetails(clientID, logout, token),
          fetchPlatformSummary(clientID, logout, token),
          fetchEncoders(token, logout),
          fetchPersistentStreams(token, logout),
        ]);
      } catch (err) {
        console.error('Error fetching data:', err);
      }
    };

    loadData();
  }, [clientID, getAccessTokenSilently, fetchUsers, fetchDetails, fetchPlatformSummary, fetchEncoders, fetchPersistentStreams, fetchClients, clients.length]);

  const handleCopyClientID = async () => {
    await navigator.clipboard.writeText(clientID);
    setClientIDCopied(true);
    setTimeout(() => setClientIDCopied(false), 2000);
  };

  const handleCopySummary = async () => {
    const text = buildClientSummary({
      clientID,
      name: client?.name ?? null,
      timeZone: client?.timeZone ?? null,
      details,
      contacts: primaryContacts,
      encoders: { online: onlineEncoders.length, total: clientEncoders.length },
      persistentStreamCount: clientPersistentStreams.length,
      platform: platformSummary,
    });
    try {
      await navigator.clipboard.writeText(text);
      toast.success('Client summary copied');
    } catch {
      toast.error('Could not copy to clipboard');
    }
  };

  const table = useReactTable({
    data: users,
    columns,
    getCoreRowModel: getCoreRowModel(),
    onSortingChange: setSorting,
    getSortedRowModel: getSortedRowModel(),
    state: {
      sorting,
    },
  });

  return (
    <Layout>
      <div>
        <section>
          <div className="mb-6 flex items-center gap-4">
            <Link to="/clients">
              <Button variant="ghost" size="icon">
                <ArrowLeft className="h-4 w-4" />
              </Button>
            </Link>
            <h3 className="text-2xl font-semibold flex items-center gap-3">
              <button
                type="button"
                onClick={handleCopyClientID}
                title="Copy client ID"
                className="font-mono text-muted-foreground hover:text-foreground flex items-center gap-1.5 transition-colors"
              >
                {clientID}
                {clientIDCopied ? (
                  <Check className="h-4 w-4 text-green-500" />
                ) : (
                  <Copy className="h-4 w-4 opacity-50" />
                )}
              </button>
              {client && <span>{client.name}</span>}
            </h3>
            {needsSwap && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setSwapDialogOpen(true)}
                className="gap-2"
              >
                <RefreshCw className="h-3 w-3" />
                Switch to this client
              </Button>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={handleCopySummary}
              className="gap-2 ml-auto"
            >
              <ClipboardCopy className="h-3 w-3" />
              Copy summary
            </Button>
          </div>

          <SwapClientDialog
            open={swapDialogOpen}
            onOpenChange={setSwapDialogOpen}
            targetClientID={clientID}
            targetClientName={client?.name ?? `Client ${clientID}`}
          />

          {/* Account Info Banner (full width) */}
          {details && (
            <div className="mb-6">
              <AccountInfoCard
                accountInfo={details.accountInfo}
                entAuth={details.entAuth}
              />
            </div>
          )}

          {/* Quick Links - Encoders, Persistent Streams, Pipeline Logs, Webhook Logs */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            {/* Encoders Link */}
            <Link
              to="/encoders"
              search={{ clientID }}
              className="block"
            >
              <div className="rounded-md border p-4 hover:bg-accent transition-colors">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="font-medium">Encoders</h4>
                    <p className="text-sm text-muted-foreground">
                      {onlineEncoders.length} online / {clientEncoders.length} total
                    </p>
                  </div>
                  <ArrowRight className="h-4 w-4 text-muted-foreground" />
                </div>
              </div>
            </Link>

            {/* Persistent Streams Link */}
            <Link
              to="/persistent-streams"
              search={{ clientID }}
              className="block"
            >
              <div className="rounded-md border p-4 hover:bg-accent transition-colors">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="font-medium">Persistent Streams</h4>
                    <p className="text-sm text-muted-foreground">
                      {clientPersistentStreams.length} configured
                    </p>
                  </div>
                  <ArrowRight className="h-4 w-4 text-muted-foreground" />
                </div>
              </div>
            </Link>

            {/* Pipeline Logs Link */}
            <Link
              to="/pipeline-logs"
              search={{ searchType: 'clientID', searchValue: clientID }}
              className="block"
            >
              <div className="rounded-md border p-4 hover:bg-accent transition-colors">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="font-medium">Pipeline Logs</h4>
                    <p className="text-sm text-muted-foreground">
                      View message history
                    </p>
                  </div>
                  <ScrollText className="h-4 w-4 text-muted-foreground" />
                </div>
              </div>
            </Link>

            {/* Webhook Logs Link */}
            <Link
              to="/monitoring/webhook-logs"
              search={{ clientID }}
              className="block"
            >
              <div className="rounded-md border p-4 hover:bg-accent transition-colors">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="font-medium">Webhook Logs</h4>
                    <p className="text-sm text-muted-foreground">
                      {details?.webhooks.length
                        ? `${details.webhooks.length} configured`
                        : 'View delivery history'}
                    </p>
                  </div>
                  <Webhook className="h-4 w-4 text-muted-foreground" />
                </div>
              </div>
            </Link>
          </div>

          {/* Platform summary + Primary contacts */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            {platformSummary && (
              <PlatformSummaryCard clientID={clientID} summary={platformSummary} />
            )}
            <ContactsCard contacts={primaryContacts} />
          </div>

          {/* Package / Billing (full width) */}
          {details && details.package && (
            <div className="mb-6">
              <PackageCard
                packageInfo={details.package}
                transcription={details.transcription}
              />
            </div>
          )}

          {/* Webhooks & API Keys */}
          {details && (details.webhooks.length > 0 || details.api) && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              <WebhooksCard webhooks={details.webhooks} />
              <ApiKeysCard api={details.api} />
            </div>
          )}

          {/* Empty state notice when no enriched data */}
          {details && !details.accountInfo && !details.package && !details.api && details.webhooks.length === 0 && details.techs.length === 0 && details.vendors.length === 0 && (
            <div className="rounded-md border border-dashed p-4 mb-6 text-center text-muted-foreground">
              <p className="text-sm">No additional client information has been populated yet.</p>
            </div>
          )}

          {/* Users Section */}
          <div className="flex justify-between items-center mb-4">
            <h4 className="text-lg font-medium">Users</h4>
            <CacheTimestamp cachedAt={usersCacheInfo?.cachedAt ?? null} />
          </div>

          {usersLoading && <p>Loading users...</p>}

          {usersError && (
            <div className="p-4 bg-red-100 border-l-4 border-red-600 rounded mt-4">
              <p className="m-0 text-red-800 font-medium">Error: {usersError}</p>
            </div>
          )}

          {!usersLoading && !usersError && users.length === 0 && (
            <p>No users found for this client.</p>
          )}

          {!usersLoading && !usersError && users.length > 0 && (
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  {table.getHeaderGroups().map((headerGroup) => (
                    <TableRow key={headerGroup.id}>
                      {headerGroup.headers.map((header) => (
                        <TableHead
                          key={header.id}
                          className={(header.column.columnDef.meta as { className?: string })?.className}
                        >
                          {header.isPlaceholder
                            ? null
                            : flexRender(
                                header.column.columnDef.header,
                                header.getContext()
                              )}
                        </TableHead>
                      ))}
                    </TableRow>
                  ))}
                </TableHeader>
                <TableBody>
                  {table.getRowModel().rows.map((row) => (
                    <TableRow key={row.id}>
                      {row.getVisibleCells().map((cell) => (
                        <TableCell
                          key={cell.id}
                          className={(cell.column.columnDef.meta as { className?: string })?.className}
                        >
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext()
                          )}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}

          {/* Techs & Vendors */}
          {details && (details.techs.length > 0 || details.vendors.length > 0) && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
              <TechsCard techs={details.techs} />
              <VendorsCard vendors={details.vendors} />
            </div>
          )}

          {/* Details Loading/Error States */}
          {detailsLoading && (
            <p className="text-sm text-muted-foreground mt-4">Loading client details...</p>
          )}
          {detailsError && (
            <div className="p-4 bg-red-100 border-l-4 border-red-600 rounded mt-4">
              <p className="m-0 text-red-800 font-medium">Error loading details: {detailsError}</p>
            </div>
          )}
        </section>
      </div>
    </Layout>
  );
}
